import { Router, UrlTree, ActivatedRouteSnapshot, RouterStateSnapshot } from "@angular/router";
import { inject } from "@angular/core";
import { AuthService } from '@services/auth/auth.service';
import { StaticDataService } from "@services/static-data/static-data.service";
import { sortByProperties } from "@utilities/arrays/arrays";
import { UserService } from "@services/user/user.service";
import { HttpErrorResponse, HttpStatusCode } from "@angular/common/http";
import { Organisation } from "src/types/organisation";

import { TenantAuthService } from '@services/auth/tenant-auth.service';
import { tenantSlugFromPath, tenantUrl } from '@services/auth/tenant-routing';

const NO_ACCESS_URL = 'no-access';
let ACCESS_CHECKED = false;
let NO_ACCESS = false;

export class AuthGuard {
    public static tenantRouteGuard(_route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean | UrlTree {
        const slug = tenantSlugFromPath(state.url);
        const selected = inject(TenantAuthService).selected;
        if (slug && selected?.companyId.toLowerCase() === slug) { return true; }
        // A full reload resolves the new pool before permissions or other cached data are loaded.
        if (slug && tenantSlugFromPath(window.location.pathname) !== slug) {
            window.location.assign(state.url);
            return false;
        }
        // Unknown tenants may still reach the login page and its generic discovery error.
        if (slug && (state.url.split(/[?#]/)[0] === `/${slug}/login` || state.url.split(/[?#]/)[0] === `/${slug}/logout`)) { return true; }
        return inject(Router).createUrlTree([slug ? tenantUrl('/login', slug) : '/login']);
    }

    public static async canLoadApp(): Promise<UrlTree | boolean> {
        ACCESS_CHECKED = true;
        NO_ACCESS = false;

        const router = inject(Router);
        const userService = inject(UserService);
        const staticDataService = inject(StaticDataService);

        const validationResult = await AuthGuard.validateUser(router);
        if (validationResult != null) { return validationResult; }

        let selectedLegalEntity: Organisation | undefined;

        try {
            selectedLegalEntity = await userService.getSelectedLegalEntity();
        }
        catch (err) {
            if (err instanceof HttpErrorResponse && err.status == HttpStatusCode.Forbidden) { return AuthGuard.navigateToNoAccess(router); }

            throw err;
        }

        if (!selectedLegalEntity) {
            const legalEntities = await userService.getAvailableLegalEntities();
            
            if (legalEntities.length === 0) {
                console.error('The user does not have access to any legal entities');
                return AuthGuard.navigateToNoAccess(router);
            }

            const firstTenantOrg = sortByProperties(legalEntities, ['id'], true)[0];
            await userService.setSelectedLegalEntity(firstTenantOrg.id.toString());
        }

        await staticDataService.refreshStaticData();
        return true;
    }

    public static async canSelectOrganisation(): Promise<UrlTree | boolean> {
        const router = inject(Router);
        const validationResult = await AuthGuard.validateUser(router);
        if (validationResult != null) { return validationResult; }

        return true;
    }

    public static async noAccessGuard(): Promise<UrlTree | boolean> {
        const router = inject(Router);
        return ACCESS_CHECKED && NO_ACCESS
            ? true
            : router.createUrlTree([tenantUrl('/app')]);
    }

    private static async validateUser(router: Router): Promise<UrlTree | boolean | null> {
        const authService = inject(AuthService);
        if (!await authService.isSignedIn()) {
            return router.createUrlTree([tenantUrl('/login')], {
                queryParams: { returnUrl: router.getCurrentNavigation()?.initialUrl.toString() ?? tenantUrl('/app') }
            });
        }
        return null;
    }

    private static navigateToNoAccess(router: Router): UrlTree | boolean {
        NO_ACCESS = true;
        // User does not have access to any orgs
        return router.url.endsWith(NO_ACCESS_URL) ? true : router.createUrlTree([tenantUrl(NO_ACCESS_URL)]);
    }
}
